import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as admin from '../../../admin'
import * as dms from '../../../dms'

import * as gql from './gql'
import type { Context, TypeParams } from './types'

@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link admin.editingVariables.Feature} */
  private readonly _editingVariablesFeature = ngCore.inject(
    admin.editingVariables.Feature,
  )

  /* ---------------------------- Standard methods ---------------------------- */

  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => (context ? gql.queries.widgets.execute(context) : rxjs.of([])),
  )

  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs.of([
      // Query and variables are not very useful to present in most contexts
      // where attributes are used
      ec.models.Attribute.fromKey<TypeParams>('id', { name: 'ID' }),
      ec.models.Attribute.fromKey<TypeParams>('alias', { name: 'Alias' }),
      ec.models.Attribute.fromKey<TypeParams>('name', { name: 'Name' }),
      ec.models.Attribute.fromKey<TypeParams>('description', {
        name: 'Description',
      }),
    ])

  public get: ec.models.Api<TypeParams>['get'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.queries.widget.executor({ ...context, widgetIdentifier: { id } })
  }

  public create: ec.models.Api<TypeParams>['create'] = (context, input) => {
    TypeAsserter.assertIsNotNullish(context)
    const variables =
      this._editingVariablesFeature.selectors.definitionsList.asSignal()
    input = {
      ...input,
      variables,
    }
    return gql.mutations.createWidget.executor({
      ...context,
      input: gql.inputs.widget.INPUT.getGqlFrom(input),
    })
  }

  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    const variables =
      this._editingVariablesFeature.selectors.definitionsList.asSignal()
    updates = {
      ...updates,
      variables,
    }
    return gql.mutations.updateWidget.executor({
      ...context,
      updates: gql.inputs.widget.UPDATES.getGqlFrom(updates),
      widgetIdentifier: { id },
    })
  }

  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.deleteWidget.executor({
      ...context,
      widgetIdentifier: { id },
    })
  }

  /* ------------------------------ Extra methods ----------------------------- */

  /**
   * If given a {@link ents.core.mosaic.Widget.Id}, this attempts to get
   * the current {@link ents.core.mosaic.Widget.Rendering} based on the
   * corresponding {@link ents.core.mosaic.Widget} stored on the backend
   *
   * If given a {@link ents.core.mosaic.Widget.Query}, this attempts to get
   * a {@link ents.core.mosaic.Widget.Rendering} by executing that query
   *
   * In other words, if the query on the frontend does not correspond to the
   * currently stored query on the backend, you should pass the query to get the
   * latest rendering. Otherwise, you can just pass the ID and the query will be
   * retrieved from the backend.
   */
  public preview(
    context: Context,
    {
      flexibleIdentifier,
      query,
    }: {
      readonly flexibleIdentifier: ents.core.common.FlexibleIdentifier<ec.models.EntityId>
      readonly query?: ents.core.mosaic.Widget.Query
    },
  ): rxjs.Observable<unknown> {
    TypeAsserter.assertIsNotNullish(context)
    return query
      ? dms.common.gql.queries.data
          .execute({
            ...context,
            query: JSON.stringify({
              ...query,
              // TODO:
              // variables: widget.variables,
            }),
          })
          .pipe(
            rxjs.map(data => {
              TypeAsserter.assertIsObject(data)
              return {
                ...data,
                ...flexibleIdentifier,
              }
            }),
          )
      : gql.queries.widgetPreview.execute({
          ...context,
          widgetIdentifier: flexibleIdentifier,
        })
  }
}
